import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import styles from "../modes.module.css";
import AITutor from '../AITutor/AITutor';
import BackButton from '../common/BackButton/BackButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, apiRequest } from '../../config/api';
import Loader from '../common/Loader/Loader';

const GRAMMAR_TOPICS = {
    TENSES: 'tenses',
    SUBJECT_VERB_AGREEMENT: 'subject_verb_agreement',
    MODIFIERS: 'modifiers',
    ARTICLES: 'articles',
    PREPOSITIONS: 'prepositions',
    PRONOUNS: 'pronouns',
    CONDITIONALS: 'conditionals',
    PASSIVE_VOICE: 'passive_voice',
    REPORTED_SPEECH: 'reported_speech',
    CONJUNCTIONS: 'conjunctions'
  };

const CorrectionMode = () => {
  const { userProgress, loading: userLoading, error: userError, updateProgress } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get difficulty from navigation state or use default
  const difficulty = location.state?.difficulty || userProgress?.modeLevels?.correction || 1;

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [topicSelectionComplete, setTopicSelectionComplete] = useState(false);
  const [loadingNextQuestion, setLoadingNextQuestion] = useState(false);
  const questionsGenerated = useRef(0);
  const [sessionId, setSessionId] = useState(null);
  const [completingSession, setCompletingSession] = useState(false);
  const initialized = useRef(false);
  const attemptedQuestions = useRef([]);

  // Reset selected answer when moving to a new question
  useEffect(() => {
    setSelectedAnswer(null);
  }, [currentQuestion]);

  // Available topics for selection
  const availableTopics = [
    'Punctuation', 'Subject-Verb Agreement', 'Verb Tense', 
    'Pronouns', 'Modifiers', 'Parallel Structure',
    'Commonly Confused Words', 'Run-on Sentences', 'Fragments'
  ];

  const handleTopicSelection = (topic) => {
    if (selectedTopics.includes(topic)) {
      setSelectedTopics(selectedTopics.filter(t => t !== topic));
    } else {
      setSelectedTopics([...selectedTopics, topic]);
    }
  };

  const startSession = async () => {
    if (selectedTopics.length === 0) {
      setError("Please select at least one topic");
      return;
    }

    setTopicSelectionComplete(true);
    setLoading(true);

    try {
      // Start a new session for correction mode
      const sessionData = await apiRequest('/sessions/start', {
        method: 'POST',
        body: JSON.stringify({
          mode: 'correction',
          difficulty: userProgress?.currentLevel || 1,
          questions: [] // Start with empty questions, we'll add them as we go
        })
      });

      setSessionId(sessionData._id);
      localStorage.setItem('currentSessionId', sessionData._id);
      
      // Start generating questions
      await generateNextQuestion();
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setTopicSelectionComplete(false); // Allow user to try again
    }
  };

  const generateNextQuestion = async () => {
    setLoadingNextQuestion(true);
    try {
      // Use userProgress.currentLevel or default to 1 if not available
      const difficulty = userProgress?.currentLevel || 1;
      
      // Determine which topic to generate for
      const topicIndex = Math.floor(questionsGenerated.current / 3);
      
      // If we've generated all questions for all topics, stop
      if (topicIndex >= selectedTopics.length) {
        setLoadingNextQuestion(false);
        return;
      }
      
      const currentTopic = selectedTopics[topicIndex];
      
      // Generate the question
      const questionData = await apiRequest(`/questions/generateOne?type=error_identification&difficulty=${difficulty}&category=grammar&topic=${currentTopic}&mode=correction`);
      
      // Add the new question to our questions array
      setQuestions(prevQuestions => [...prevQuestions, questionData]);
      questionsGenerated.current++;
      
      // If this is the first question, stop loading
      if (questionsGenerated.current === 1) {
        setLoading(false);
      }
      
      // Continue generating questions until we have all questions (3 per topic)
      if (questionsGenerated.current < selectedTopics.length * 3) {
        // Generate next question in the background
        setTimeout(() => generateNextQuestion(), 500);
      }
    } catch (err) {
      console.error("Error generating question:", err);
      // Don't set error here to avoid interrupting the session
      // Just continue with what we have
    } finally {
      setLoadingNextQuestion(false);
    }
  };

  const handleAnswer = async () => {
    if (isSubmitting || !selectedAnswer) return;
    
    // Check if this is the last generated question and we're still generating more
    const isLastGeneratedQuestion = currentQuestion === questions.length - 1;
    const stillGeneratingQuestions = questionsGenerated.current < selectedTopics.length * 3;
    
    if (isLastGeneratedQuestion && stillGeneratingQuestions && loadingNextQuestion) {
      // Don't allow submission yet - we're still generating the next question
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Submit the answer for current question using apiRequest
      const result = await apiRequest(`/sessions/${sessionId}/individual-answer`, {
        method: 'POST',
        body: JSON.stringify({
          question: questions[currentQuestion], // Send the complete question object
          answer: selectedAnswer,
          timeSpent: 0 // You might want to track time spent per question
        })
      });

      const newAnswers = { ...answers, [currentQuestion]: selectedAnswer };
      setAnswers(newAnswers);
      attemptedQuestions.current.push({...questions[currentQuestion], _id: result.id});
      // If this was the last question, end the session
      if (currentQuestion + 1 >= selectedTopics.length * 3) {
        setCompletingSession(true); // Show loading overlay
        
        const results = await apiRequest(`/sessions/${sessionId}/end`, {
          method: 'POST'
        });

        localStorage.removeItem('currentSessionId');
        updateProgress(results);
        navigate('/results', { state: { sessionResults: results, questions: attemptedQuestions.current } });
      } else {
        // Move to next question
        setCurrentQuestion(currentQuestion + 1);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render topic selection UI if topics haven't been selected yet
  if (!topicSelectionComplete) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h2 className={styles.title}>Select Topics to Practice</h2>
          <p className={styles.subtitle}>Choose one or more grammar topics you want to focus on:</p>
          
          <div className={styles.topicsGrid}>
            {availableTopics.map(topic => (
              <button
                key={topic}
                className={`${styles.topicButton} ${selectedTopics.includes(topic) ? styles.selected : ''}`}
                onClick={() => handleTopicSelection(topic)}
              >
                {topic}
              </button>
            ))}
          </div>
          
          {error && <div className={styles.error}>{error}</div>}
          
          <button 
            className={styles.startButton}
            onClick={startSession}
            disabled={selectedTopics.length === 0}
          >
            Start Practice
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        <Loader size="large" text="Preparing your questions..." />
      </div>
    );
  }

  if (error && !topicSelectionComplete) {
    return (
      <div className={styles.error}>
        <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <span>{error}</span>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className={styles.loading}>
        <p>Generating your first question...</p>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / (selectedTopics.length * 3)) * 100;
  const currentTopicIndex = Math.floor(currentQuestion / 3);
  const currentTopic = selectedTopics[currentTopicIndex];
  const questionInTopic = (currentQuestion % 3) + 1;

  return (
    <div className={styles.container}>
      {completingSession && (
        <div className={styles.overlay}>
          <Loader size="large" text="Submitting answers..." />
        </div>
      )}
      
      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>Correction Mode</h2>
          <div className={styles.questionInfo}>
            <span className={styles.questionCount}>
              Question {currentQuestion + 1} of {selectedTopics.length * 3}
            </span>
          </div>
        </div>
        
        <div className={styles.progressContainer}>
          <div 
            className={styles.progressBar} 
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className={styles.questionText}>
          {currentQ.content.text}
        </div>
        
        <div className={styles.optionsContainer}>
          {currentQ.content.options.map((option, index) => (
            <button
              key={index}
              onClick={() => setSelectedAnswer(option)}
              className={`${styles.optionButton} ${
                selectedAnswer === option ? styles.selected : ''
              }`}
              disabled={isSubmitting}
            >
              {option}
            </button>
          ))}
        </div>
        
        <div className={styles.navigationButtons}>
          <button 
            onClick={handleAnswer}
            className={styles.nextButton}
            disabled={
              isSubmitting || 
              !selectedAnswer || 
              (currentQuestion === questions.length - 1 && 
               questionsGenerated.current < selectedTopics.length * 3 && 
               loadingNextQuestion)
            }
          >
            {currentQuestion + 1 === selectedTopics.length * 3 ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>

      <div className={styles.footer}>
        <p className={styles.footerText}>
          Take your time to identify the correct answer.
        </p>
      </div>
    </div>
  );
};

export default CorrectionMode;